/**
 * Application Configuration
 * Following constitution requirements for ArbiWin platform
 *
 * This file contains application-wide configuration settings
 * that are shared across all modules and components.
 */

// Import environment configuration
import { getEnvironmentConfig } from './environment';

/**
 * Core application configuration
 */
export const APP_CONFIG = {
  // Application metadata
  name: 'ArbiWin',
  version: '1.0.0',
  description: 'Subscription-based sports betting arbitrage analysis platform for Bulgarian users',

  // Default language settings (Bulgarian primary)
  defaultLanguage: 'bg',
  supportedLanguages: ['bg', 'en'],

  // Timezone configuration
  timezone: 'Europe/Sofia',
  dateFormat: 'DD.MM.YYYY',
  timeFormat: 'HH:mm',

  // Session configuration
  session: {
    tokenExpiryMinutes: 30,
    refreshWarningMinutes: 5,
    autoLogoutOnExpiry: true,
    rememberLoginDays: 30
  },

  // UI configuration
  ui: {
    theme: 'dark',
    primaryColor: '#53d22d',
    animationDuration: 300,
    debounceDelay: 300,
    loadingTimeout: 30000,
    toastDuration: 5000
  },

  // Data refresh intervals (following constitution)
  dataRefresh: {
    surebetsAutoRefreshMs: 600000, // 10 minutes
    subscriptionCheckMs: 300000,   // 5 minutes
    staleDataWarningMs: 900000     // 15 minutes
  },

  // Pagination and limits
  pagination: {
    defaultPageSize: 20,
    maxPageSize: 100,
    maxSurebetsDisplay: 500
  },

  // Validation rules
  validation: {
    passwordMinLength: 8,
    emailMaxLength: 255,
    stakeMinAmount: 1.0,
    stakeMaxAmount: 10000.0,
    profitThreshold: 0.01 // 1%
  },

  // Performance settings
  performance: {
    enableWebVitals: true,
    lazyLoadRoutes: true,
    memoizeComponents: true,
    virtualizeThreshold: 100
  }
};

/**
 * Get application configuration with environment overrides
 * @returns {Object} Complete application configuration
 */
export function getAppConfig() {
  const envConfig = getEnvironmentConfig();

  return Object.assign({}, APP_CONFIG, {
    // Override with environment-specific settings
    api: envConfig.api,
    features: envConfig.features,
    debug: envConfig.debug,

    // Override data refresh intervals if specified
    dataRefresh: Object.assign({}, APP_CONFIG.dataRefresh, {
      surebetsAutoRefreshMs: envConfig.autoRefreshInterval || APP_CONFIG.dataRefresh.surebetsAutoRefreshMs,
      staleDataWarningMs: envConfig.staleDataWarning || APP_CONFIG.dataRefresh.staleDataWarningMs
    }),

    // Override validation rules if specified
    validation: Object.assign({}, APP_CONFIG.validation, {
      stakeMinAmount: envConfig.minStakeAmount || APP_CONFIG.validation.stakeMinAmount,
      stakeMaxAmount: envConfig.maxStakeAmount || APP_CONFIG.validation.stakeMaxAmount,
      profitThreshold: envConfig.profitThreshold || APP_CONFIG.validation.profitThreshold
    })
  });
}

/**
 * Get specific configuration section
 * @param {string} section - Configuration section name
 * @returns {Object} Configuration section
 */
export function getConfigSection(section) {
  const config = getAppConfig();
  return config[section] || {};
}

/**
 * Check if feature is enabled
 * @param {string} featureName - Feature name to check
 * @returns {boolean} True if feature is enabled
 */
export function isFeatureEnabled(featureName) {
  const config = getAppConfig();
  return config.features && config.features[featureName] === true;
}

/**
 * Get localized configuration values
 * @param {string} language - Language code (bg/en)
 * @returns {Object} Localized configuration
 */
export function getLocalizedConfig(language) {
  const config = getAppConfig();
  language = language || config.defaultLanguage;

  return {
    language: language,
    dateFormat: language === 'bg' ? 'DD.MM.YYYY' : 'MM/DD/YYYY',
    timeFormat: config.timeFormat,
    timezone: config.timezone,
    currency: 'EUR',
    currencySymbol: '€'
  };
}

/**
 * Development mode helpers
 */
export const isDevelopment = function() {
  return process.env.NODE_ENV === 'development';
};

export const isProduction = function() {
  return process.env.NODE_ENV === 'production';
};

export const isTest = function() {
  return process.env.NODE_ENV === 'test';
};

/**
 * Application constants for consistent usage
 */
export const APP_CONSTANTS = {
  STORAGE_KEYS: {
    AUTH_TOKEN: 'arbiwin_auth_token',
    USER_PREFERENCES: 'arbiwin_user_preferences',
    LANGUAGE: 'arbiwin_language',
    BOOKMAKER_FILTERS: 'arbiwin_bookmaker_filters',
    LAST_REFRESH: 'arbiwin_last_refresh'
  },

  DEFAULT_VALUES: {
    LANGUAGE: 'bg',
    CURRENCY: 'BGN',
    TIMEZONE: 'Europe/Sofia',
    STAKE_AMOUNT: 100,
    AUTO_REFRESH: true
  },

  LIMITS: {
    MAX_LOGIN_ATTEMPTS: 5,
    MAX_RETRY_ATTEMPTS: 3,
    MAX_CONCURRENT_REQUESTS: 10,
    MAX_ERROR_LOGS: 100
  }
};

export default APP_CONFIG;
